import { getDocLang } from "Helper/metaHelper";

// Class representing a month in the calendar. like a Date but only has year and month. (no day, hours, minutes etc)
export default class Month {
  // first day of the month
  private _firstDay: Date;

  constructor(value?: Date | string | undefined | Month) {
    // e.g. "2022-12-02"
    if (typeof value === "string") {
      // "YYYY-MM"
      if (/^\d\d\d\d-\d\d$/.test(value)) value += "-01";
      value = new Date(value);
    } else if (typeof value === "undefined") value = new Date();
    else if (value instanceof Month) value = value.firstDay;

    this._firstDay = new Date(value);
    this._firstDay.setDate(1);
    this._firstDay.setHours(0);
    this._firstDay.setMinutes(0);
    this._firstDay.setSeconds(0);
    this._firstDay.setMilliseconds(0);
  }

  // e.g. `2022-01` for january 2022
  toString() {
    const month = String(this._firstDay.getMonth() + 1).padStart(2, "0");
    const year = String(this._firstDay.getFullYear());

    return `${year}-${month}`;
  }

  // allows comparison by simply using the iso formatted value were it starts with year then month
  valueOf() {
    return this.toString();
  }

  // both month objects representing the sae calendar month
  equals(month: Month) {
    return month.toString() === this.toString();
  }

  // e.g. `2022`
  get year(): number {
    return this._firstDay.getFullYear();
  }

  // Date of the first day in this month
  get firstDay() {
    // return a clone to prevent unintended mutations from the consumer
    return new Date(this._firstDay);
  }

  // Date of the last day in this month
  get lastDay() {
    const d = new Date(this._firstDay);
    d.setMonth(this._firstDay.getMonth() + 1);
    // will go back to the last day of previous month
    d.setDate(0);
    return d;
  }

  // list of all days in this month as Date[]
  get days() {
    const year = this._firstDay.getFullYear();
    const month = this._firstDay.getMonth();
    return (
      Array.from({ length: this.lastDay.getDate() })
        .fill(null)
        // starting with `1`
        .map((_, ix) => new Date(year, month, ix + 1))
    );
  }

  // e.g. "April 2022"
  toLocaleString(lang = getDocLang()) {
    return this._firstDay.toLocaleString(lang, { year: "numeric", month: "long" });
  }

  /**
   * month is moved forwards or backwards in time
   *
   * @param {number} val - how many moths the current month is moved (can be + or -)
   * @returns {Month} - the mutated month, to allow chaining. e.g. `month.change(+1) > otherMonth`
   * @example
   *   month.change(-2); // go two months back
   */
  change(val: number) {
    this._firstDay.setMonth(this._firstDay.getMonth() + val);
    return this;
  }

  /**
   * returns a clone of the current month instance which has the same date, but mutating it, won't have any effect on the original Month instance
   * @returns {Month}
   */
  clone() {
    return new Month(this._firstDay);
  }
}
